import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { BatchSubmissionService } from './batch-submission.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-batch-submission',
    templateUrl: './batch-submission.component.html',
    styleUrls: ['./batch-submission.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BatchSubmissionComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subs = new Subscription();
  private isInitialized = false;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  env: string = 'TEST';
  canDoProdSubmit: boolean = false;
  submitting: boolean = false;
  subIds: string = '';
  currentTheme: string = 'theme-black';
  minimise: boolean = false;

  showProdConfirmation: boolean = false;
  prodConfirmationStep: number = 0;

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private batchSubmissionService: BatchSubmissionService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.SERVER_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.currentTheme = this.USER_SETTINGS?.user?.selected_theme || 'theme-black';
    this.canDoProdSubmit = this.SERVER_CONFIG?.clientConfig?.efile_prod_submission_users
      ? this.SERVER_CONFIG.clientConfig.efile_prod_submission_users.includes(this.USER_SETTINGS?.user?.sso_id)
      : false;
    this.subIds = this.rowData.subIds || '';
    this.cdr.detectChanges();
  }

  submit(): void {
    if (this.env !== 'TEST') {
      this.showProdConfirmation = true;
      this.prodConfirmationStep = 1;
      this.cdr.detectChanges();
      return;
    }
    this.doSubmit();
  }

  confirmProdSubmission(): void {
    if (this.prodConfirmationStep === 1) {
      this.prodConfirmationStep = 2;
      this.cdr.detectChanges();
      return;
    }
    this.showProdConfirmation = false;
    this.prodConfirmationStep = 0;
    this.doSubmit();
  }

  cancelProdConfirmation(): void {
    this.showProdConfirmation = false;
    this.prodConfirmationStep = 0;
    this.cdr.detectChanges();
  }

  private doSubmit(): void {
    this.submitting = true;
    this.cdr.detectChanges();

    this.batchSubmissionService.submitBatch(this.baseURLs.efile, this.subIds, this.env).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.submitting = false;
        if (res.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Successfully submitted to IRS!', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar('Failed to submit to IRS!', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.submitting = false;
        this.gtwSnackbarService.openSnackBar('Error submitting to IRS. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
